"use client";

import Link from "next/link";
import { useId, useState } from "react";
import type { HubAreaCountry, HubAreaGroup, HubAreaPill } from "@/lib/conditions/hub/types";
import { HubChevron } from "./hub-icons";

/**
 * The F4 continent accordion for one Explore by area tab. One continent open at a time, the
 * first by default. Inside, each country lists its area pills: a region pill (shaded) followed by
 * the areas inside it. On phone a continent with several countries nests one more accordion level
 * (one country open at a time) with sideways-scrolling pill rails; on desktop every country row
 * of the open continent shows. Closed panels stay in the HTML (hidden), so every area link is
 * crawlable.
 */
function areasLabel(count: number): string {
  return `${count} ${count === 1 ? "area" : "areas"}`;
}

type PillKind = "region" | "top" | "child";

const PILL_BASE =
  "flex min-h-11 items-center gap-[7px] whitespace-nowrap rounded-full border px-3 text-navy no-underline transition-colors hover:border-navy hover:text-navy focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark md:gap-1.5";

const PILL_KIND: Readonly<Record<PillKind, string>> = {
  region: "border-border bg-warm-bg text-[13.5px] font-semibold md:border-[#D5DBD2] md:bg-cream md:font-bold",
  top: "border-border bg-warm-bg text-[13.5px] font-semibold md:bg-white",
  child: "border-dashed border-[#C9D0C6] bg-white text-[12.5px] font-medium md:border-solid md:border-border md:text-[13.5px] md:font-semibold",
};

function AreaPillLink({ pill, kind, parent, unit }: { pill: HubAreaPill; kind: PillKind; parent: string | null; unit: string }) {
  const label = `${pill.name}${kind === "region" ? ", region" : parent ? `, part of ${parent}` : ""}, ${pill.count} ${unit}`;
  return (
    <Link href={pill.href} prefetch={false} aria-label={label} className={`${PILL_BASE} ${PILL_KIND[kind]}`}>
      {pill.name}
      <span className={`font-semibold text-[#566072] ${kind === "child" ? "text-[11.5px] md:text-[12.5px]" : "text-[12px] md:text-[12.5px]"}`}>
        {pill.count}
      </span>
    </Link>
  );
}

function countryPills(country: HubAreaCountry, unit: string, rail: boolean) {
  const item = rail ? "shrink-0 snap-start md:shrink" : undefined;
  return country.groups.flatMap((group) => {
    const parent = group.region?.name ?? null;
    return [
      ...(group.region
        ? [<li key={group.region.slug} className={item}><AreaPillLink pill={group.region} kind="region" parent={null} unit={unit} /></li>]
        : []),
      ...group.areas.map((area) => (
        <li key={area.slug} className={item}>
          <AreaPillLink pill={area} kind={parent ? "child" : "top"} parent={parent} unit={unit} />
        </li>
      )),
    ];
  });
}

function CountryRows({
  continent,
  unit,
  baseId,
}: {
  continent: HubAreaGroup;
  unit: string;
  baseId: string;
}) {
  const [openCountry, setOpenCountry] = useState<string | null>(null);
  const nested = continent.countries.length > 1;
  return (
    <>
      {continent.countries.map((country, index) => {
        const listLabel = `${continent.continentLabel}, ${country.countryName} areas`;
        const open = openCountry === country.countryCode;
        const buttonId = `${baseId}-${country.countryCode}-btn`;
        const panelId = `${baseId}-${country.countryCode}-panel`;
        return (
          <div
            key={country.countryCode}
            className={`${index > 0 ? "border-t border-[#EDF1EC] md:border-dashed md:border-border" : ""} md:grid md:grid-cols-[136px_minmax(0,1fr)] md:items-start md:gap-3 md:py-[5px]`}
          >
            <p className="m-0 hidden min-h-11 items-center gap-1.5 text-[13.5px] font-bold text-[#454C5A] md:flex">
              {country.countryName}
              <span className="font-semibold text-[#566072]">{country.areaCount}</span>
            </p>
            {nested && (
              <h4 className="m-0 md:hidden">
                <button
                  type="button"
                  id={buttonId}
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => setOpenCountry(open ? null : country.countryCode)}
                  className="flex min-h-12 w-full cursor-pointer items-center justify-between gap-2.5 rounded-[10px] border-0 bg-transparent p-1 text-left font-sans text-navy transition-colors hover:bg-[#F4F8F7] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark"
                >
                  <span className="min-w-0 text-[14.5px] font-semibold leading-[1.25] text-navy">{country.countryName}</span>
                  <span className="flex shrink-0 items-center gap-2.5">
                    <span className="text-[12.5px] font-semibold text-[#566072]">{areasLabel(country.areaCount)}</span>
                    <span
                      aria-hidden="true"
                      className={`flex text-teal-dark transition-transform duration-200 motion-reduce:transition-none ${open ? "rotate-90" : ""}`}
                    >
                      <HubChevron direction="right" size={14} />
                    </span>
                  </span>
                </button>
              </h4>
            )}
            <div
              id={panelId}
              role={nested ? "region" : undefined}
              aria-labelledby={nested ? buttonId : undefined}
              className={nested ? `${open ? "block" : "hidden"} pb-2.5 pt-0.5 md:block md:p-0` : "pt-0.5 md:p-0"}
            >
              <ul
                aria-label={listLabel}
                className={`m-0 flex list-none gap-1.5 p-0 md:flex-wrap md:overflow-visible ${
                  nested
                    ? "-mx-2.5 snap-x snap-proximity scroll-pl-2.5 overflow-x-auto px-2.5 pb-0.5 md:mx-0 md:px-0 md:pb-0"
                    : "flex-wrap"
                }`}
              >
                {countryPills(country, unit, nested)}
              </ul>
            </div>
          </div>
        );
      })}
    </>
  );
}

export function BrowseByAreaAccordion({
  continents,
  unit,
}: {
  continents: readonly HubAreaGroup[];
  /** "surf spots" or "resorts". */
  unit: string;
}) {
  const baseId = useId();
  const [openContinent, setOpenContinent] = useState<string | null>(continents[0]?.continentId ?? null);
  return (
    <>
      {continents.map((continent, index) => {
        const open = continent.continentId === openContinent;
        const buttonId = `${baseId}-${continent.continentId}-btn`;
        const panelId = `${baseId}-${continent.continentId}-panel`;
        const preview = continent.countries.map((country) => country.countryName).join(", ");
        return (
          <div key={continent.continentId} className={index > 0 ? "border-t border-[#EDF1EC]" : undefined}>
            <h3 className="m-0">
              <button
                type="button"
                id={buttonId}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpenContinent(open ? null : continent.continentId)}
                className="flex min-h-14 w-full cursor-pointer items-center justify-between gap-2.5 border-0 bg-transparent px-3.5 py-2 text-left font-sans text-navy transition-colors hover:bg-[#F4F8F7] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-teal-dark md:justify-start md:gap-3 md:bg-white md:px-5 md:py-0"
              >
                <span className="flex min-w-0 flex-col gap-0.5 md:contents">
                  <span className="text-[16px] font-bold leading-[1.2] text-navy md:text-[17px]">{continent.continentLabel}</span>
                  <span className="truncate text-[12.5px] leading-[1.3] text-[#566072] md:hidden">{preview}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2.5 md:contents">
                  <span className="inline-flex h-6 items-center rounded-full bg-[#EEF1EA] px-[9px] text-[12px] font-bold text-[#454C5A] md:h-auto md:bg-cream md:py-0.5 md:text-[12.5px]">
                    {areasLabel(continent.areaCount)}
                  </span>
                  <span className="hidden min-w-0 flex-auto truncate text-[13.5px] font-medium text-[#566072] md:block">
                    {open ? "" : preview}
                  </span>
                  <span
                    aria-hidden="true"
                    className={`flex text-teal-dark transition-transform duration-200 motion-reduce:transition-none md:hidden ${open ? "rotate-90" : ""}`}
                  >
                    <HubChevron direction="right" />
                  </span>
                  <span
                    aria-hidden="true"
                    className={`hidden size-8 shrink-0 items-center justify-center rounded-full text-teal-dark transition-transform duration-200 motion-reduce:transition-none md:flex ${
                      open ? "rotate-180 bg-[#E4F2F3]" : "bg-warm-bg"
                    }`}
                  >
                    <HubChevron direction="down" size={18} />
                  </span>
                </span>
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              className={`${open ? "block" : "hidden"} ${
                continent.countries.length > 1 ? "px-2.5 pb-2" : "px-3 pb-3.5"
              } md:px-5 md:pb-3.5`}
            >
              <CountryRows continent={continent} unit={unit} baseId={`${baseId}-${continent.continentId}`} />
            </div>
          </div>
        );
      })}
    </>
  );
}
